import { useId } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, RefreshCw } from 'lucide-react';
import { Input, Skeleton, Spinner, cn } from '@eombor/ui';
import { motionEase, motionSeconds } from '../../../shared/ui/motion.js';
import { t } from '../../../shared/i18n/index.js';
import type { CaptchaState } from '../model/useCaptcha.js';

/**
 * CAPTCHA maydoni — BITTA qator: rasm, kod maydoni va yangilash tugmasi.
 *
 * Uchalasi ham maydon balandligida (`--size-control`) va pastdan tekislanadi.
 * Ilgari rasm balandroq turardi, "Yangi rasm" yozuvi esa uning tagida
 * qo'shimcha qator ochardi — natijada blok formadagi boshqa maydonlardan
 * ikki baravar baland bo'lib, qatorlar bir-biriga nisbatan siljib ketardi.
 *
 * TASDIQLANGACH YO'QOLADI. O'rniga bitta yupqa qator qoladi ("tasdiqlandi")
 * va odam faqat parol bilan ovora bo'ladi: rasm ishini bajarib bo'ldi,
 * ekranda qolib turishi esa faqat joy egallardi.
 *
 * Rasm SVG bo'lib keladi va `dangerouslySetInnerHTML` bilan chiziladi — bu
 * YAGONA joy va u ataylab: `<img>` ichidagi `data:` URI mavzu bilan birga
 * rangini o'zgartira olmasdi (SVG dagi `currentColor` shu tufayli ishlaydi).
 * Manba — o'z serverimiz, tarkibi esa faqat `path`, `circle` va `text`.
 */
export interface CaptchaFieldProps {
  captcha: CaptchaState;
}

export const CaptchaField = ({ captcha }: CaptchaFieldProps) => {
  const errorId = useId();
  const reduceMotion = useReducedMotion() === true;
  const transition = {
    duration: reduceMotion ? 0 : motionSeconds('--motion-base', 220),
    ease: motionEase('--ease-out-quick', [0.16, 1, 0.3, 1]),
  };

  return (
    <AnimatePresence initial={false} mode="wait">
      {captcha.token !== null ? (
        <motion.p
          key="captcha-done"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={transition}
          className={cn(
            // Tasdiqlangan holat — YASHIL: bu bajarilgan ish, harakatga
            // chorlov emas. Ko'k tint uni hali bosilishi kerak bo'lgan
            // narsaga o'xshatib qo'yardi.
            'border-success/25 bg-success-soft text-text flex items-center gap-2',
            'h-[var(--size-control)] overflow-hidden rounded-[var(--radius-sm)] border px-3 text-sm',
          )}
        >
          <Check size={15} aria-hidden className="text-success shrink-0" />
          {t('login.captchaDone')}
        </motion.p>
      ) : (
        <motion.div
          key="captcha-form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, height: 0 }}
          transition={transition}
          /*
            `overflow-hidden` chiqib ketish animatsiyasi (balandlik → 0)
            uchun kerak, lekin u FOKUS HALQASINI ham kesib tashlaydi.
            `p-1 -m-1` unga joy beradi va joylashuvni o'zgartirmaydi.
          */
          className="flex flex-col gap-1.5 overflow-hidden p-1 -m-1"
        >
          {/* `items-end` — maydonning ustida yorlig'i turadi, shuning uchun
              qatorni PASTDAN tekislash kerak. */}
          <div className="flex items-end gap-2">
            <div
              className={cn(
                'border-border bg-surface-2 text-text flex shrink-0 items-center justify-center',
                'h-[var(--size-control)] w-[8.5rem] overflow-hidden',
                'rounded-[var(--radius-sm)] border select-none',
              )}
            >
              {captcha.loading ? (
                <Skeleton className="h-5 w-24" />
              ) : captcha.svg === null ? (
                <span className="text-text-faint text-[0.7rem]">{t('login.captchaFailed')}</span>
              ) : (
                <span
                  aria-hidden
                  className="block h-full w-full [&>svg]:h-full [&>svg]:w-full"
                  dangerouslySetInnerHTML={{ __html: captcha.svg }}
                />
              )}
            </div>

            {/*
              Xato matni `Input` ning ICHIDA emas, qatordan PASTDA chiziladi.
              Ichida bo'lsa, ikki qatorli xabar maydonni cho'zib yuborardi va
              yonidagi rasm bilan tugma o'z joyidan siljib ketardi — qator
              buzilib ko'rinardi.
            */}
            <Input
              label={t('login.captcha')}
              className={cn('flex-1', captcha.error === null ? undefined : '[&_input]:border-danger')}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={5}
              /* Yangi rasm yuklanayotganda ham qulflanadi: ekranda rasm yo'q,
                 ya'ni bu paytda terilgan kod baribir rad etilardi. */
              disabled={captcha.checking || captcha.loading}
              value={captcha.answer}
              onChange={(event) => captcha.setAnswer(event.target.value)}
              aria-invalid={captcha.error === null ? undefined : true}
              aria-describedby={captcha.error === null ? undefined : errorId}
              suffix={captcha.checking ? <Spinner size="sm" /> : undefined}
            />

            {/* Faqat belgi: yozuv qatorni uzaytirar va rasm bilan maydonni
                siqib qo'yardi. Nomi `aria-label` va `title` da qoladi. */}
            <button
              type="button"
              onClick={captcha.reload}
              aria-label={t('login.captchaReload')}
              title={t('login.captchaReload')}
              className={cn(
                'border-border text-text-faint hover:text-text hover:border-border-strong',
                'flex size-[var(--size-control)] shrink-0 items-center justify-center',
                'rounded-[var(--radius-sm)] border',
                'transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
              )}
            >
              <RefreshCw size={15} aria-hidden />
            </button>
          </div>

          {captcha.error === null ? null : (
            <p id={errorId} role="alert" className="text-danger text-sm text-pretty">
              {captcha.error}
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
